import { addDoc, deleteDoc, doc, getDoc, getDocs, query, updateDoc } from 'firebase/firestore';
import { generateModifierOptionId, ModifierGroup, modifierGroupsRef } from '@api/ModifierGroups/ModifierGroup';
import DishServ from '@api/Dishes/DishServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { buildCreationStamp } from '@api/ActivityLogs/CreationStamp';
import { logCatalogChange, MODIFIER_GROUP_FIELDS } from '@api/ActivityLogs/CatalogActivity';
import { publishCatalog } from '@api/Catalog/CatalogVersion';

export default {
    async saveModifierGroup(modifierGroup: ModifierGroup): Promise<ModifierGroup> {
        // Firestore no acepta undefined: normalizamos opciones. El id de cada opción se genera
        // una sola vez y NO cambia después (las líneas de orden v2 lo referencian).
        modifierGroup.options = (modifierGroup.options || []).map((option) => ({
            id: option.id || generateModifierOptionId(),
            name: option.name,
            recipeId: option.recipeId,
            quantityPerSelection: Number(option.quantityPerSelection) || 0,
            extraCost: Number(option.extraCost) || 0,
            status: option.status ?? true
        }));
        const isNew = !modifierGroup.id;
        const before = isNew ? null : await this.getModifierGroup(modifierGroup.id as string);
        if (isNew) {
            const data = await addDoc(modifierGroupsRef, { ...modifierGroup, ...(await buildCreationStamp()) });
            modifierGroup.id = data.id;
        }
        await updateDoc(doc(modifierGroupsRef, modifierGroup.id), { ...modifierGroup });
        await publishCatalog('modifier-group');
        await logCatalogChange('modifier-group', modifierGroup.id, before, modifierGroup, MODIFIER_GROUP_FIELDS);
        return modifierGroup;
    },
    async getModifierGroups(): Promise<ModifierGroup[]> {
        const modifierGroups: ModifierGroup[] = [];
        const queryDoc = query(modifierGroupsRef);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return modifierGroups;
        }
        snapshot.docs.map((doc) => {
            const modifierGroup = doc.data() as ModifierGroup;
            modifierGroup.id = doc.id;
            modifierGroups.push(modifierGroup);
        });
        modifierGroups.sort((a, b) => a.name.localeCompare(b.name, 'es'));
        return modifierGroups;
    },
    async getModifierGroup(modifierGroupId: string): Promise<ModifierGroup> {
        const documentData = await getDoc(doc(modifierGroupsRef, modifierGroupId));
        const modifierGroup = documentData.data() as ModifierGroup;
        if (modifierGroup) {
            modifierGroup.id = modifierGroupId;
        }
        return modifierGroup;
    },
    // Firestore no puede consultar dentro de arrays de objetos: se filtra en cliente
    // (la colección es chica, mismo trade-off que el resto del sistema)
    async getModifierGroupsByRecipeId(recipeId: string): Promise<ModifierGroup[]> {
        const modifierGroups = await this.getModifierGroups();
        return modifierGroups.filter((group) => (group.options || []).some((option) => option.recipeId === recipeId));
    },
    async deleteModifierGroup(modifierGroupId: string): Promise<void> {
        // Candado de integridad: no dejar productos apuntando a grupos borrados
        const dishesUsingGroup = await DishServ.getDishesByModifierGroupId(modifierGroupId);
        if (dishesUsingGroup.length > 0) {
            const dishNames = dishesUsingGroup.map((dish) => dish.name).join(', ');
            throw new Error(
                `El modificador está en uso por los productos: ${dishNames}. Quita esas referencias antes de eliminarlo.`
            );
        }
        await deleteDoc(doc(modifierGroupsRef, modifierGroupId));
        await publishCatalog('modifier-group-delete');
        await ActivityLogServ.addActivityLog('modifier-group', modifierGroupId, 'eliminó el grupo de modificadores');
        return;
    }
};
